import { useCallback, useRef, useState } from "react";
import type { Notification } from "../types";

/** Most toasts visible at once; bursts drop the oldest. */
const MAX_TOASTS = 3;

export interface UseNotificationToasts {
  /** Currently visible toasts, newest first. */
  readonly toasts: Notification[];
  /** Show a toast for a freshly arrived notification. */
  readonly push: (notification: Notification) => void;
  /** Remove a toast (auto-dismiss, close button, or click-through). */
  readonly dismiss: (id: number) => void;
}

/**
 * Owns the transient top-right toast queue, fed by the `onNotification`
 * callback of {@link useNotifications} (which fires once per live SignalR push).
 *
 * Dedupes by id because that callback runs outside the state updater and can
 * fire for an id that was already toasted (e.g. a duplicate live push). The
 * durable bell/inbox state is unaffected — this is purely the ephemeral pop-up.
 */
export function useNotificationToasts(): UseNotificationToasts {
  const [toasts, setToasts] = useState<Notification[]>([]);
  const seenRef = useRef<Set<number>>(new Set());

  const push = useCallback((notification: Notification) => {
    if (seenRef.current.has(notification.id)) {
      return;
    }
    seenRef.current.add(notification.id);
    setToasts((prev) => [notification, ...prev].slice(0, MAX_TOASTS));
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { toasts, push, dismiss };
}
